export const MAX_SIZE = 1500;
export const MIN_SIZE = 600;

const hasValues = object => object && !Object.values(object).includes(0);
const getArrayDimensions = object => [object.width, object.height];

export function getWrapper(containerId, maxWidth, maxHeight) {
  const imageWrapper = containerId && document.getElementById(containerId);

  if (imageWrapper) {
    return {
      width: imageWrapper.offsetWidth,
      height: imageWrapper.offsetHeight
    };
  }

  return {
    width: maxWidth,
    height: maxHeight
  };
}

export function getScaledDimensions(wrapper, original) {
  if (!hasValues(wrapper)) {
    return { width: 0, height: 0 };
  }

  if (!hasValues(original)) {
    return wrapper;
  }

  const factor = Math.min(wrapper.width / original.width, wrapper.height / original.height);

  return {
    width: factor * original.width,
    height: factor * original.height
  };
}

export function resizeByRatio(width, height, maxSize = MAX_SIZE) {
  const aspectRatio = width / height;
  if (aspectRatio > 1) {
    return [maxSize, maxSize / aspectRatio];
  }

  return [maxSize * aspectRatio, maxSize];
}

export function downsizeDimensions(originalWidth, originalHeight) {
  let zoomWidth = originalWidth;
  let zoomHeight = originalHeight;
  let downsized = false;
  if (Math.max(originalWidth, originalHeight) > MAX_SIZE) {
    downsized = true;
    [zoomWidth, zoomHeight] = resizeByRatio(originalWidth, originalHeight, MAX_SIZE);
  }

  return {
    width: zoomWidth,
    height: zoomHeight,
    downsized
  };
}

export function getZoomDimensions(image, largeImage, wrapper) {
  const { width, height } = getScaledDimensions(wrapper, image);
  let zoom = largeImage || [0, 0];

  if (!Array.isArray(zoom)) {
    zoom = getArrayDimensions(zoom);
  }

  if (Math.max(...zoom) < Math.max(width, height)) {
    zoom = [2 * width, 2 * height];
  }

  const isRotated =
    (width >= height && zoom[0] < zoom[1]) || (width < height && zoom[0] >= zoom[1]);
  const originalWidth = zoom[isRotated ? 1 : 0];
  const originalHeight = zoom[isRotated ? 0 : 1];

  return downsizeDimensions(originalWidth, originalHeight);
}

export function createCropSource(src, smallImage, largeImage, noResize = false) {
  if (noResize && (largeImage.width < MIN_SIZE || largeImage.height < MIN_SIZE)) {
    return null;
  }

  const regex = /rs=w:\d+,h:\d+/;
  const hasResize = regex.test(src);
  const pfx = hasResize ? '' : '/:';
  const { width, height, downsized } = downsizeDimensions(largeImage.width, largeImage.height);
  const { width: w, height: h } = getScaledDimensions({ width, height }, smallImage);
  let source = `${src}${pfx}/cr=w:${w},h:${h},a:cc`;

  if (noResize && (w < smallImage.width || h < smallImage.height)) {
    return null;
  }

  if (noResize && downsized) {
    if (hasResize) {
      source = source.replace(regex, `rs=w:${MAX_SIZE},h:${MAX_SIZE}`);
    } else {
      source = source.replace('/:', `/:/rs=w:${MAX_SIZE},h:${MAX_SIZE},cg:true`);
    }
  }

  return {
    src: source,
    width: w,
    height: h
  };
}

export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onerror = error => reject(error);
    image.onload = () =>
      resolve({
        src,
        width: image.width || 0,
        height: image.height || 0
      });
    image.src = src;
  });
}
